import type { ApiResult } from '@/services/apiClient';
import { apiClient } from '@/services/apiClient';
import type {
  AiChatApproval,
  AiChatAttachment,
  AiChatBootstrap,
  AiChatContext,
  AiChatMessage,
  AiChatSendResult,
  AiChatThread,
  AiChatToolCall,
  AiChatVoiceResult,
} from './aiChat.types';

const BASE = '/ai-chat';

function confirmedInsuranceReply(data: unknown): boolean {
  if (!data || typeof data !== 'object' || !('assistantMessage' in data)) return false;
  const message = data.assistantMessage;
  if (!message || typeof message !== 'object' || !('metadata' in message)) return false;
  const metadata = message.metadata;
  return Boolean(
    metadata &&
      typeof metadata === 'object' &&
      'protectedContext' in metadata &&
      metadata.protectedContext === 'insurance',
  );
}

export const aiChatApi = {
  bootstrap(): Promise<ApiResult<AiChatBootstrap>> {
    return apiClient.get(`${BASE}/bootstrap`);
  },

  listThreads(): Promise<ApiResult<AiChatThread[]>> {
    return apiClient.get(`${BASE}/threads`);
  },

  createThread(input: { title?: string } = {}): Promise<ApiResult<AiChatThread>> {
    return apiClient.post(`${BASE}/threads`, input);
  },

  updateThread(
    threadId: string,
    input: { title?: string; pinned?: boolean; voiceAutoPlay?: boolean },
  ): Promise<ApiResult<AiChatThread>> {
    return apiClient.patch(`${BASE}/threads/${encodeURIComponent(threadId)}`, input);
  },

  listMessages(
    threadId: string,
    params: { cursor?: string; limit?: number } = {},
  ): Promise<ApiResult<{ items: AiChatMessage[]; nextCursor: string | null }>> {
    const search = new URLSearchParams();
    if (params.cursor) search.set('cursor', params.cursor);
    if (params.limit) search.set('limit', String(params.limit));
    const query = search.toString();
    return apiClient.get(
      `${BASE}/threads/${encodeURIComponent(threadId)}/messages${query ? `?${query}` : ''}`,
    );
  },

  async sendMessage(
    threadId: string,
    input: {
      content: string;
      context?: AiChatContext;
      attachmentIds?: string[];
      projectId?: string;
      regionId?: string;
      inputModality?: 'TEXT' | 'VOICE';
      speakReply?: boolean;
    },
  ): Promise<ApiResult<AiChatSendResult>> {
    const path = input.context === 'insurance' ? 'insurance-messages' : 'messages';
    const result = await apiClient.post<AiChatSendResult>(
      `${BASE}/threads/${encodeURIComponent(threadId)}/${path}`,
      input,
    );
    if (input.context === 'insurance' && result.ok) {
      if (!confirmedInsuranceReply(result.data)) {
        return {
          ...result,
          ok: false,
          data: null,
          error:
            'Insurance context was not confirmed. Refresh before asking again; do not automatically resend.',
        };
      }
    }
    return result;
  },

  listApprovals(input: { toolCallId?: string } = {}): Promise<ApiResult<AiChatApproval[]>> {
    const query = input.toolCallId ? `?toolCallId=${encodeURIComponent(input.toolCallId)}` : '';
    return apiClient.get(`${BASE}/approvals${query}`);
  },

  presignAttachment(input: {
    threadId: string;
    fileName: string;
    mimeType: string;
    sizeBytes: number;
    sha256: string;
    kind?: 'FILE' | 'IMAGE' | 'AUDIO';
  }): Promise<
    ApiResult<{
      attachment: AiChatAttachment;
      upload: { url: string; requiredHeaders: Record<string, string>; expiresAt: string };
    }>
  > {
    return apiClient.post(`${BASE}/attachments/presign`, input);
  },

  completeAttachment(
    attachmentId: string,
    input: { sha256?: string } = {},
  ): Promise<ApiResult<AiChatAttachment>> {
    return apiClient.post(
      `${BASE}/attachments/${encodeURIComponent(attachmentId)}/complete`,
      input,
    );
  },

  generateImage(input: { threadId: string; prompt: string }): Promise<ApiResult<AiChatMessage>> {
    return apiClient.post(`${BASE}/images/generate`, input);
  },

  async transcribeAndSend(input: {
    threadId: string;
    attachmentId: string;
    speakReply?: boolean;
    sendMessage?: boolean;
    projectId?: string;
    regionId?: string;
    context?: AiChatContext;
  }): Promise<ApiResult<AiChatVoiceResult>> {
    // Older servers strip unknown sendMessage flags and send immediately. Review
    // must use a distinct route that fails closed while those servers roll out.
    const autoSend = input.sendMessage === true;
    const path = !autoSend
      ? 'transcribe-review'
      : input.context === 'insurance'
        ? 'transcribe-insurance'
        : 'transcribe';
    const result = await apiClient.post<AiChatVoiceResult>(`${BASE}/voice/${path}`, input);
    if (
      autoSend &&
      input.context === 'insurance' &&
      result.ok &&
      !confirmedInsuranceReply(result.data)
    ) {
      return {
        ...result,
        ok: false,
        data: null,
        error:
          'Insurance context was not confirmed. Refresh before asking again; do not automatically resend.',
      };
    }
    if (!autoSend && result.ok) {
      const data = result.data;
      if (
        !data ||
        typeof data !== 'object' ||
        !('kind' in data) ||
        data.kind !== 'transcript' ||
        typeof data.transcript !== 'string' ||
        data.attachmentId !== input.attachmentId
      ) {
        return {
          ...result,
          ok: false,
          data: null,
          error:
            'Voice review returned an unexpected response. Check the conversation before retrying.',
        };
      }
    }
    return result;
  },

  speakMessage(input: { messageId: string }): Promise<ApiResult<AiChatAttachment>> {
    return apiClient.post(`${BASE}/voice/speak`, input);
  },

  approveToolCall(
    toolCallId: string,
    input: { note?: string } = {},
  ): Promise<ApiResult<AiChatToolCall>> {
    return apiClient.post(`${BASE}/tool-calls/${encodeURIComponent(toolCallId)}/approve`, input);
  },

  denyToolCall(
    toolCallId: string,
    input: { note?: string } = {},
  ): Promise<ApiResult<AiChatToolCall>> {
    return apiClient.post(`${BASE}/tool-calls/${encodeURIComponent(toolCallId)}/deny`, input);
  },
};

export async function sha256Hex(file: Blob): Promise<string> {
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export function putToPresignedUrl(
  url: string,
  requiredHeaders: Record<string, string>,
  file: Blob,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('PUT', url, true);
    for (const [header, value] of Object.entries(requiredHeaders)) {
      request.setRequestHeader(header, value);
    }
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        resolve();
        return;
      }
      reject(new Error(`Object storage rejected the upload (${request.status})`));
    };
    request.onerror = () => reject(new Error('Upload failed to reach storage'));
    request.send(file);
  });
}
